import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Sparkles, Check, Plus, Wand2 } from 'lucide-react';
import ImageDownloadMenu from './ImageDownloadMenu';
import ReviseLogoDialog from './ReviseLogoDialog';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ withCredentials: true });

function LogoCard({ vision, versions, onUse, using, onRevise }) {
  const [vi, setVi] = useState(versions.length - 1);
  useEffect(() => { setVi(versions.length - 1); }, [versions.length]);
  const c = versions[Math.min(vi, versions.length - 1)];
  const url = c.asset_url || `${BACKEND}/api/website-vision/${vision.vision_id}/logo-concept/${c.concept_id}`;
  const chosen = vision.chosen_logo === c.concept_id;
  return (
    <div className={`rounded-xl border-2 p-2 bg-white transition-colors ${chosen ? 'border-[#1D4ED8]' : 'border-[#E2E8F0]'}`} data-testid={`logo-concept-${c.concept_id}`}>
      <a href={url} target="_blank" rel="noopener noreferrer"><img src={url} alt={c.style} className="w-full aspect-square object-contain rounded-lg bg-white" /></a>
      <div className="text-[10px] font-bold uppercase text-[#94A3B8] mt-1 truncate">{c.style}{c.revision ? ` · ${c.revision}` : ''}</div>
      {versions.length > 1 && (
        <div className="flex flex-wrap gap-1 mt-1" data-testid={`logo-versions-${versions[0].concept_id}`}>
          {versions.map((v, i) => (
            <button key={v.concept_id} type="button" onClick={() => setVi(i)} data-testid={`logo-version-${v.concept_id}`} title={v.revision || 'Original'}
              className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${i === vi ? 'bg-[#0A0F1C] text-white' : 'bg-slate-100 text-[#475569] hover:bg-slate-200'}`}>v{v.version || i + 1}</button>
          ))}
        </div>
      )}
      <div className="flex gap-1 mt-1.5">
        <ImageDownloadMenu visionId={vision.vision_id} srcId={c.asset_id || c.concept_id} />
        <button type="button" onClick={() => onRevise(c, url)} data-testid={`logo-revise-${c.concept_id}`}
          className="flex-1 inline-flex items-center justify-center gap-1 py-1.5 rounded-lg text-xs font-bold border border-[#F97316] text-[#C2410C] hover:bg-[#FFF7ED]"><Wand2 size={12} /> Revise</button>
      </div>
      {onUse && (
        <button type="button" onClick={() => onUse(c.concept_id)} disabled={chosen || !!using} data-testid={`logo-use-${c.concept_id}`}
          className={`mt-1 w-full inline-flex items-center justify-center gap-1 py-1.5 rounded-lg text-xs font-bold ${chosen ? 'bg-[#EFF6FF] text-[#1D4ED8]' : 'bg-[#0A0F1C] text-white hover:bg-[#1e293b]'} disabled:opacity-80`}>
          {using === c.concept_id ? <Loader2 size={12} className="animate-spin" /> : chosen ? <Check size={12} /> : null} {chosen ? 'Your logo' : 'Use this logo'}
        </button>
      )}
    </div>
  );
}

export default function LogoConcepts({ vision, base, q, name, onUpdate, maxRoots = 14, allowCreate = true }) {
  const [busy, setBusy] = useState(vision.logo_status === 'generating');
  const [using, setUsing] = useState('');
  const [revising, setRevising] = useState(null);
  const pollRef = useRef(null);
  const all = vision.logo_concepts || [];
  const groups = {};
  all.forEach((c) => { const r = c.parent_id || c.concept_id; (groups[r] = groups[r] || []).push(c); });
  const rootIds = all.filter((c) => !c.parent_id).map((c) => c.concept_id);
  const fromAsset = new Set(all.map((c) => c.from_asset).filter(Boolean));
  const uploaded = (vision.assets || []).filter((a) => a.kind === 'logo' && !a.from_concept && !fromAsset.has(a.asset_id))
    .map((a) => ({ concept_id: a.asset_id, asset_id: a.asset_id, style: 'Your uploaded logo', asset_url: `${BACKEND}${a.url}` }));
  const generatedRoots = all.filter((c) => !c.parent_id && !c.from_asset).length;
  useEffect(() => () => clearInterval(pollRef.current), []);

  const poll = () => {
    const started = Date.now();
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      const r = await ax.get(`${BACKEND}${base}/${vision.vision_id}${q}`).catch(() => null);
      if (!r) return;
      if (r.data.logo_status !== 'generating' || Date.now() - started > 240000) {
        clearInterval(pollRef.current); setBusy(false); onUpdate(r.data);
        if (r.data.logo_status === 'ready') toast.success('Your logos are ready');
        else toast.error(r.data.logo_error || 'This is taking too long, please try again.');
      }
    }, 4000);
  };
  useEffect(() => { if (vision.logo_status === 'generating') poll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const create = async (count) => {
    setBusy(true);
    try { await ax.post(`${BACKEND}${base}/${vision.vision_id}/logo-concepts${q}`, { count, name }); poll(); }
    catch (e) { setBusy(false); toast.error(e.response?.data?.detail || 'Could not start'); }
  };
  const use = async (cid) => {
    setUsing(cid);
    try { const r = await ax.post(`${BACKEND}${base}/${vision.vision_id}/logo-concepts/${cid}/use${q}`); onUpdate(r.data); toast.success('Logo saved, brand colors pulled from it'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not save logo'); }
    finally { setUsing(''); }
  };
  const revise = async (body) => {
    try {
      await ax.post(`${BACKEND}${base}/${vision.vision_id}/logo-concepts/${revising.c.concept_id}/revise${q}`, body);
      setRevising(null); setBusy(true); poll(); toast.success('Revising your logo… (about 30 sec)');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not revise'); }
  };

  return (
    <div data-testid="logo-concepts">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2"><Sparkles size={15} className="text-[#F97316]" /><h5 className="font-bold text-sm text-[#0A0F1C]">Logo studio</h5>
          <span className="text-[11px] text-[#94A3B8]">for “{name || 'your brand'}”</span></div>
        {allowCreate && (generatedRoots === 0 ? (
          <button type="button" onClick={() => create(Math.min(4, maxRoots))} disabled={busy || !name} data-testid="logo-create-btn"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#F97316] text-white text-sm font-bold hover:bg-[#EA580C] disabled:opacity-60">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Create {Math.min(4, maxRoots)} logo concepts
          </button>
        ) : generatedRoots < maxRoots && (
          <button type="button" onClick={() => create(3)} disabled={busy || !name} data-testid="logo-more-btn"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-[#F97316] text-[#C2410C] text-sm font-bold hover:bg-[#FFF7ED] disabled:opacity-60">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} More concepts
          </button>
        ))}
      </div>
      {busy && <p className="text-xs text-[#1D4ED8] mb-2 inline-flex items-center gap-1.5" data-testid="logo-status"><Loader2 size={12} className="animate-spin" /> Working on your logo… (about 30–60 sec)</p>}
      {(rootIds.length > 0 || uploaded.length > 0) && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {uploaded.map((u) => <LogoCard key={u.concept_id} vision={vision} versions={[u]} onRevise={(c, url) => setRevising({ c, url })} />)}
          {rootIds.map((rid) => (
            <LogoCard key={rid} vision={vision} versions={groups[rid]} onUse={use} using={using} onRevise={(c, url) => setRevising({ c, url })} />
          ))}
        </div>
      )}
      <ReviseLogoDialog open={!!revising} onClose={() => setRevising(null)} imgUrl={revising?.url}
        palettes={(vision.concept?.palettes) || (vision.brand?.palette?.length ? [{ name: 'Your palette', colors: vision.brand.palette }] : [])} onSubmit={revise} />
    </div>
  );
}
